import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../../common/auth";
import { C, DISPLAY_FONT } from "../../common/theme";
import { Promotion, PromotionDetail, createPromotion, updatePromotion } from "../../common/requests/promotions/endpoints";
import { DetailList, errorMessage, useIsNarrow, useToast } from "../../ui";
import VoucherPreview from "./VoucherPreview";
import {
  CLAIM_BASE_URL,
  FIELD_STEP,
  FUNDING_LABEL,
  FormErrors,
  PromoForm,
  SAFE_FIELDS,
  STEPS,
  TRIGGER_LABEL,
  buyerPreview,
  datesLabel,
  emptyForm,
  formFromPromotion,
  howLabel,
  money,
  offerLabel,
  quickCheck,
  toInput,
  typeLabel,
  validate,
} from "./promoText";

interface PromotionDialogProps {
  open: boolean;
  /** Edit this promotion; omit to create one */
  promotion?: Promotion | null;
  onClose: () => void;
  onSaved: (saved: PromotionDetail) => void;
}

type TypeChoice = "voucher" | "free_delivery" | "new_buyer";

const TTL_OPTIONS = [
  { value: "24", label: "24 hours" },
  { value: "48", label: "48 hours" },
  { value: "72", label: "3 days" },
  { value: "168", label: "7 days" },
  { value: "720", label: "30 days" },
  { value: "", label: "The promotion's end date" },
];

const WINDOW_OPTIONS = [
  { value: "", label: "ever" },
  { value: "30", label: "every 30 days" },
  { value: "90", label: "every 90 days" },
  { value: "365", label: "every year" },
];

/** A radio drawn as a bordered card with a line of explanation. */
function Choice({ value, title, body, disabled }: { value: string; title: string; body: string; disabled?: boolean }) {
  return (
    <FormControlLabel
      value={value}
      disabled={disabled}
      control={<Radio size="small" sx={{ mt: -0.25 }} />}
      label={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.25 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 14 }}>
            {title}
          </Typography>
          <Typography component="span" variant="caption" sx={{ color: C.muted, lineHeight: 1.4 }}>
            {body}
          </Typography>
        </Box>
      }
      sx={{
        m: 0,
        alignItems: "flex-start",
        border: `1px solid ${C.line2}`,
        borderRadius: "8px",
        p: 1.25,
        pr: 1.5,
        flex: "1 1 180px",
        minWidth: 0,
        bgcolor: C.paper,
        "&:has(input:checked)": { borderColor: C.ink, boxShadow: `inset 0 0 0 1px ${C.ink}` },
      }}
    />
  );
}

function Choices({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <FormControl component="fieldset" sx={{ minWidth: 0 }}>
      <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 600, color: C.ink, mb: 0.75, "&.Mui-focused": { color: C.ink } }}>
        {label}
      </FormLabel>
      <RadioGroup aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1 }}>
        {children}
      </RadioGroup>
    </FormControl>
  );
}

const grid = (min = 180) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 1.5 });

/** New promotion / edit: five steps (Type, Offer, Who gets it, Limits & who
 * pays, Dates & review) with the buyer's voucher previewed as you type.
 * Published promotions only change name, title, dates and limits. */
export default function PromotionDialog({ open, promotion, onClose, onSaved }: PromotionDialogProps) {
  const { idToken } = useAuth();
  const toast = useToast();
  const narrow = useIsNarrow(900);
  const phone = useIsNarrow(600);
  const editing = !!promotion;
  const published = editing && promotion!.status !== "draft";
  const [form, setForm] = React.useState<PromoForm>(emptyForm);
  const [step, setStep] = React.useState(0);
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setForm(promotion ? formFromPromotion(promotion) : emptyForm());
    setStep(promotion ? 1 : 0);
    setErrors({});
    setServerError(null);
  }, [open, promotion]);

  const set = <K extends keyof PromoForm>(key: K, value: PromoForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };
  const locked = (key: keyof PromoForm) => published && !SAFE_FIELDS.includes(key);
  const input = toInput(form);
  const preview = buyerPreview(input);
  const check = quickCheck(input);
  const last = step === STEPS.length - 1;

  const typeChoice: TypeChoice = form.promo_type === "free_delivery" ? "free_delivery" : form.first_order_only ? "new_buyer" : "voucher";
  const setType = (v: string) => {
    setForm((f) => ({ ...f, promo_type: v === "free_delivery" ? "free_delivery" : "voucher", first_order_only: v === "new_buyer" ? true : v === "voucher" ? false : f.first_order_only }));
  };

  const firstProblem = (errs: FormErrors) => Math.min(...Object.keys(errs).map((k) => FIELD_STEP[k as keyof PromoForm]));

  const next = () => {
    const errs = validate(form, { step });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const save = async (publish: boolean) => {
    const errs = validate(form, { publish: publish || published });
    setErrors(errs);
    if (Object.keys(errs).length) {
      setStep(firstProblem(errs));
      return;
    }
    setBusy(true);
    setServerError(null);
    try {
      const res = editing ? await updatePromotion(promotion!.id, input, publish, idToken!) : await createPromotion(input, publish, idToken!);
      toast(publish ? "Promotion published." : published ? "Changes saved." : "Draft saved.", "success");
      onSaved(res.data);
    } catch (err: any) {
      const message = errorMessage(err, "Couldn't save the promotion.");
      const field = err?.response?.headers?.["x-field"] as keyof PromoForm | undefined;
      if (field && field in FIELD_STEP) {
        setErrors({ [field]: message });
        setStep(FIELD_STEP[field]);
      } else {
        setServerError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const field = (key: keyof PromoForm) => ({
    value: form[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(key, e.target.value as any),
    error: !!errors[key],
    helperText: errors[key],
    disabled: locked(key) || busy,
    fullWidth: true,
    size: "small" as const,
  });
  const sgd = { input: { startAdornment: <InputAdornment position="start">S$</InputAdornment> } };

  const stepBody = [
    // 1 · Type
    <Box key="type" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Choices label="What kind of promotion?" value={typeChoice} onChange={setType}>
        <Choice value="voucher" title="Voucher" body="S$ off or % off above a minimum spend, on any order or only some sellers or genera." disabled={locked("promo_type")} />
        <Choice value="free_delivery" title="Free delivery" body="Takes delivery off, up to a cap. Can be used with one discount voucher." disabled={locked("promo_type")} />
        <Choice value="new_buyer" title="New-buyer voucher" body="A voucher for a buyer's first order only." disabled={locked("first_order_only")} />
      </Choices>
      <Typography variant="caption" sx={{ color: C.muted }}>
        Sale prices and flash sales come later.
      </Typography>
    </Box>,
    // 2 · Offer
    <Box key="offer" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField label="Name (only you see this)" {...field("name")} autoFocus={!editing} />
      {form.promo_type === "voucher" && (
        <Choices label="Discount" value={form.discount_kind} onChange={(v) => set("discount_kind", v as any)}>
          <Choice value="amount" title="S$ off" body="A fixed amount." disabled={locked("discount_kind")} />
          <Choice value="percent" title="% off, with a cap" body="A share of the plants, up to a most." disabled={locked("discount_kind")} />
        </Choices>
      )}
      <Box sx={grid(160)}>
        {form.promo_type === "free_delivery" ? (
          <TextField label="Delivery covered, up to" {...field("max_discount")} inputMode="decimal" slotProps={sgd} />
        ) : form.discount_kind === "percent" ? (
          <>
            <TextField label="Percent off" {...field("amount")} inputMode="numeric" slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }} />
            <TextField label="Most it takes off" {...field("max_discount")} inputMode="decimal" slotProps={sgd} />
          </>
        ) : (
          <TextField label="Amount off" {...field("amount")} inputMode="decimal" slotProps={sgd} />
        )}
        <TextField label="Minimum spend on plants" {...field("min_spend")} inputMode="decimal" slotProps={sgd} />
      </Box>
      <Choices label="Applies to" value={form.scope} onChange={(v) => set("scope", v as any)}>
        <Choice value="all" title="All plants" body="Any seller, any genus." disabled={locked("scope")} />
        <Choice value="sellers" title="Chosen sellers" body="Their plants count towards the minimum." disabled={locked("scope")} />
        <Choice value="genera" title="Chosen genera" body="e.g. Anthurium, Hoya." disabled={locked("scope")} />
      </Choices>
      {form.scope !== "all" && (
        <TextField
          label={form.scope === "sellers" ? "Seller usernames, separated by commas" : "Genera, separated by commas"}
          {...field("scope_values")}
        />
      )}
      <FormControlLabel
        control={<Checkbox checked={form.first_order_only} onChange={(e) => set("first_order_only", e.target.checked)} disabled={locked("first_order_only") || busy} />}
        label="First order only (new buyers)"
      />
      <TextField label="Title buyers see (optional)" placeholder={buyerPreview({ ...input, title: null }).title} {...field("title")} />
    </Box>,
    // 3 · Who gets it
    <Box key="who" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Choices label="How do buyers get it?" value={form.distribution} onChange={(v) => set("distribution", v as any)}>
        <Choice value="code" title="Public code" body="Anyone types it in the cart." disabled={locked("distribution")} />
        <Choice value="claim" title="Claim link" body="A button adds it to the wallet." disabled={locked("distribution")} />
        <Choice value="given" title="Given to buyers" body="We put it in their wallet." disabled={locked("distribution")} />
      </Choices>
      {form.distribution === "code" && (
        <TextField
          label="Code"
          {...field("code")}
          onChange={(e) => set("code", e.target.value.toUpperCase().replace(/\s/g, ""))}
          helperText={errors.code ?? "Buyers type it in the cart. 3 to 20 letters or digits."}
          slotProps={{ htmlInput: { style: { textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.04em" } } }}
        />
      )}
      {form.distribution === "claim" && (
        <Box sx={grid(200)}>
          <TextField
            label="Claim link"
            {...field("claim_slug")}
            onChange={(e) => set("claim_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
            helperText={errors.claim_slug ?? `${CLAIM_BASE_URL}${form.claim_slug || "…"}`}
          />
          <TextField label="Max claims (empty = no limit)" {...field("max_claims")} inputMode="numeric" />
        </Box>
      )}
      {form.distribution !== "code" && (
        <Box sx={grid(200)}>
          {form.distribution === "given" && (
            <TextField select label="Give it when" {...field("give_trigger")}>
              {Object.entries(TRIGGER_LABEL).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField select label="Expires after" {...field("wallet_ttl_hours")} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
            {TTL_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    </Box>,
    // 4 · Limits & who pays
    <Box key="limits" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={grid(160)}>
        <TextField label="Uses per buyer" {...field("uses_per_buyer")} inputMode="numeric" />
        <TextField select label="Per buyer" {...field("per_buyer_window_days")} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
          {WINDOW_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField label="Total uses (empty = no limit)" {...field("total_uses")} inputMode="numeric" />
        <TextField label="Budget (pauses when spent)" {...field("budget")} inputMode="decimal" slotProps={sgd} />
      </Box>
      <Choices label="Who pays for the discount" value={form.funded_by} onChange={(v) => set("funded_by", v as any)}>
        <Choice value="inflos" title="Inflos" body="Comes out of our fee. Sellers paid in full." disabled={locked("funded_by")} />
        <Choice value="sellers" title="Sellers in the order" body={'Split by plant value; a "Promotion" line on their payout.'} disabled={locked("funded_by")} />
        <Choice value="split" title="Half each" body="Inflos and the sellers split it." disabled={locked("funded_by")} />
      </Choices>
      {form.funded_by !== "inflos" && (
        <Alert severity="info" icon={false} sx={{ bgcolor: C.bone1, color: C.ink }}>
          Seller-paid promotions are listed on the seller platform before they start, so sellers get the notice the Terms require.
        </Alert>
      )}
    </Box>,
    // 5 · Dates & review
    <Box key="dates" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={grid(200)}>
        <TextField type="datetime-local" label="Starts (SGT)" {...field("starts_at")} helperText={errors.starts_at ?? "Empty = as soon as it's published"} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField type="datetime-local" label="Ends (SGT)" {...field("expires_at")} helperText={errors.expires_at ?? "Empty = no end date"} slotProps={{ inputLabel: { shrink: true } }} />
      </Box>
      <DetailList
        items={[
          { label: "Type", value: typeLabel(input) },
          { label: "Offer", value: offerLabel(input) },
          { label: "Applies to", value: input.scope === "all" ? "All plants" : input.scope_values.join(", ") },
          { label: "How buyers get it", value: `${howLabel(input)}${input.code ? ` · ${input.code}` : ""}` },
          {
            label: "Limits",
            value: [
              `${input.uses_per_buyer} per buyer${input.per_buyer_window_days ? ` every ${input.per_buyer_window_days} days` : ""}`,
              input.total_uses ? `${input.total_uses} uses in total` : "no total cap",
              input.budget ? `budget ${money(input.budget)}` : "no budget",
            ].join(", "),
          },
          { label: "Who pays", value: FUNDING_LABEL[input.funded_by] },
          { label: "Dates", value: datesLabel(input.starts_at, input.expires_at) },
        ]}
      />
    </Box>,
  ];

  const rail = (
    <Box
      component="nav"
      aria-label="Steps"
      sx={{ width: 220, flexShrink: 0, bgcolor: C.head, borderRight: `1px solid ${C.line}`, p: 2.5, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 0.75 }}
    >
      <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 20, pb: 1.5 }}>{editing ? "Edit promotion" : "New promotion"}</Typography>
      {STEPS.map((label, i) => {
        const done = i < step;
        const current = i === step;
        return (
          <Box
            key={label}
            component="button"
            type="button"
            onClick={() => (i < step || editing ? setStep(i) : undefined)}
            aria-current={current ? "step" : undefined}
            sx={{
              all: "unset",
              boxSizing: "border-box",
              cursor: i < step || editing ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 1,
              fontSize: 14,
              borderRadius: "8px",
              fontWeight: current ? 700 : 500,
              color: done ? C.leaf : current ? C.ink : C.muted,
              bgcolor: current ? C.paper : "transparent",
              border: `1px solid ${current ? C.line : "transparent"}`,
              "&:focus-visible": { outline: `2px solid ${C.coral}` },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 22,
                height: 22,
                borderRadius: "11px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                bgcolor: done ? "#2E7D4F" : current ? C.ink : "transparent",
                color: done || current ? "#fff" : C.muted,
                border: done || current ? "none" : `1.5px solid ${C.faint}`,
              }}
            >
              {done ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
            </Box>
            {done && i === 0 ? `Type: ${typeLabel(input)}` : done && i === 1 ? `Offer: ${offerLabel(input)}` : label}
          </Box>
        );
      })}
    </Box>
  );

  const previewPanel = (
    <Box
      component="aside"
      aria-label="What buyers see"
      sx={{
        width: narrow ? "auto" : 290,
        flexShrink: 0,
        bgcolor: C.ground,
        p: narrow ? 2 : 2.5,
        borderLeft: narrow ? "none" : `1px solid ${C.line}`,
        borderTop: narrow ? `1px solid ${C.line}` : "none",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Typography variant="overline" sx={{ color: C.muted }}>
        What buyers see
      </Typography>
      <VoucherPreview text={preview} />
      <Typography variant="caption" sx={{ color: C.ink2, lineHeight: 1.5 }}>
        Shown in My vouchers and the cart's voucher sheet. Buyers tap Apply themselves.
      </Typography>
      {check && (
        <>
          <Typography variant="overline" sx={{ color: C.muted, mt: 1 }}>
            Quick check
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.5 }}>
            {check}
          </Typography>
        </>
      )}
    </Box>
  );

  const canPublish = !published;
  const actions = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        justifyContent: "space-between",
        alignItems: "center",
        borderTop: `1px solid ${C.line}`,
        pt: 1.5,
        pb: phone ? "calc(12px + env(safe-area-inset-bottom, 0px))" : 0,
        mt: "auto",
      }}
    >
      <Button variant="outlined" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || busy}>
        Back
      </Button>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", flex: phone ? "1 1 auto" : "0 0 auto", "& > *": { flex: phone ? "1 1 auto" : "0 0 auto" } }}>
        {published ? (
          <Button variant="contained" onClick={() => save(false)} disabled={busy}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
        ) : (
          <Button variant="outlined" onClick={() => save(false)} disabled={busy}>
            Save draft
          </Button>
        )}
        {!last && (
          <Button variant="contained" onClick={next} disabled={busy}>
            Next: {STEPS[step + 1].toLowerCase()}
          </Button>
        )}
        {last && canPublish && (
          <Button variant="contained" onClick={() => save(true)} disabled={busy}>
            {busy ? "Publishing…" : "Publish"}
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullScreen={phone}
      maxWidth={false}
      PaperProps={{ "aria-label": editing ? "Edit promotion" : "New promotion", sx: { width: phone ? "100%" : 1040, maxWidth: phone ? "100%" : "calc(100vw - 32px)", height: phone ? "100%" : "min(780px, calc(100vh - 48px))", m: phone ? 0 : undefined, borderRadius: phone ? 0 : "14px", overflow: "hidden" } }}
    >
      <Box sx={{ display: "flex", flexDirection: narrow ? "column" : "row", height: "100%", minHeight: 0, overflowY: narrow ? "auto" : "hidden" }}>
        {!narrow && rail}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", p: { xs: 2, sm: 3 }, pt: { xs: "calc(12px + env(safe-area-inset-top, 0px))", sm: 3 }, gap: 2, overflowY: narrow ? "visible" : "auto" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: C.leaf, fontWeight: 700 }}>
                Step {step + 1} of {STEPS.length}
                {narrow ? ` · ${STEPS[step]}` : ""}
              </Typography>
              <Typography variant="h5" component="h2">
                {["What kind of promotion?", "What's the offer?", "How do buyers get it?", "Limits and who pays", "Dates and review"][step]}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close" disabled={busy} sx={{ width: 44, height: 44, mr: -1, mt: -0.5 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {published && (
            <Alert severity="info" icon={false} sx={{ bgcolor: C.leafTint, color: C.ink }}>
              This promotion is published: only its name, title, dates and limits can change. Duplicate it to change the offer.
            </Alert>
          )}
          {serverError && <Alert severity="error">{serverError}</Alert>}
          {stepBody[step]}
          {!narrow && actions}
        </Box>
        {previewPanel}
        {narrow && <Box sx={{ px: 2, bgcolor: C.paper, position: "sticky", bottom: 0 }}>{actions}</Box>}
      </Box>
    </Dialog>
  );
}
